import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {TextInput} from '@interstate/components/TextInput';
import {Button} from '@interstate/components/Button';
import {Alert} from '@interstate/components/Alert';
import {useAuth} from '../context/AuthContext';
import {useHealth} from '../context/HealthContext';
import {apiUrl} from '../lib/api';

// Set VITE_BYPASS_AUTH=true in .env to skip the password gate in local dev.
const BYPASS_AUTH = import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true';

export default function PasswordGate(): React.ReactElement {
  const {login} = useAuth();
  const {isDown} = useHealth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (BYPASS_AUTH) {
      login();
      navigate('/list', {replace: true});
    }
  }, [login, navigate]);

  // Render nothing while the redirect is in flight
  if (BYPASS_AUTH) {
return <></>;
}

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(apiUrl('/auth'), {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        credentials: 'include',
        body: JSON.stringify({password})
      });
      if (res.ok) {
        login();
        navigate('/list');
      } else if (res.status === 401) {
        setError('Incorrect password. Please try again.');
        setPassword('');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-subdued flex flex-col items-center justify-center px-4 font-sans">

      {/* Brand mark */}
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2.5 mb-2">
          <div className="w-px h-5 bg-primary-700" />
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
            Cox Automotive
          </span>
        </div>
        <p className="text-xs text-gray-400 tracking-wide">Privacy &amp; Compliance</p>
      </div>

      {/* Card */}
      <div className="w-full max-w-md bg-white rounded-xl border border-gray-100 shadow-md overflow-hidden">
        <div className="h-1 bg-primary-700" />
        <div className="px-8 py-8">
          <h1 className="text-xl font-bold text-gray-900 mb-1">Sub-Processor Registry</h1>
          <p className="text-sm text-subdued mb-5">
            This registry is restricted. Enter the access password to continue.
          </p>

          {/* Legal acknowledgment */}
          <div className="border-l-4 border-primary-700 bg-gray-50 pl-4 pr-3 py-3 rounded-r-lg mb-6">
            <p className="text-xs text-gray-600 leading-relaxed">
              By clicking <strong>&ldquo;Access Registry&rdquo;</strong> below, you acknowledge
              and agree that: (a)&nbsp;the information in this registry is confidential and
              proprietary to Cox Automotive; and (b)&nbsp;you will not distribute, reproduce, or
              otherwise make available the registry contents to any person or entity outside of
              your organization.
            </p>
          </div>

          {error &&
            <Alert
              type="error"
              sx={{marginBottom: '1rem'}}
              onCloseClick={() => setError('')}
            >
              {error}
            </Alert>
          }

          <form onSubmit={handleSubmit}>
            <TextInput
              name="password"
              label="Access Password"
              type="password"
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                if (error) {
setError('');
}
              }}
              placeholder="Enter password"
              required
              hasError={!!error}
              sx={{marginBottom: '1.5rem'}}
            />
            <Button buttonStyle="primary" type="submit" block size="medium" disabled={loading || isDown}>
              {loading ? 'Verifying...' : isDown ? 'Service unavailable' : 'Access Registry'}
            </Button>
          </form>
        </div>
      </div>

      <p className="mt-5 text-xs text-gray-400">
        Need access? Contact the Privacy team.
      </p>
    </div>
  );
}
